import React, { useImperativeHandle, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Box,
    CircularProgress,
    Typography
} from '@mui/material';
import { useSettingsStore } from '@core/store';
import { useTranslation } from 'react-i18next';
import { WcnButton, WcnTextField } from '@/components/index.ts';

export interface ConfirmationDialogProps {
    // Content props
    expectedInputTexts: string[];
    heading?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;

    // Control props
    isSaving?: boolean;
    waitForConfirm?: boolean;

    // Event handlers
    onConfirm?: () => void | Promise<void>;
    onClose?: () => void;
}

export interface ConfirmationDialogRef {
    openDialog: () => void;
}

type Props = ConfirmationDialogProps & {
    dialogRef?: React.Ref<ConfirmationDialogRef>;
};

const ConfirmationDialogRequireInput = ({
    // Content props
    heading = '',
    message = '',
    confirmText = '',
    cancelText = '',
    expectedInputTexts = [],

    // Control props
    isSaving = false,
    waitForConfirm = false,

    // Event handlers
    onConfirm,
    onClose,

    // Ref prop
    dialogRef
}: Props) => {
    const [open, setOpen] = useState(false);
    const { isMobile } = useSettingsStore();
    const { t } = useTranslation();
    const [confirmationTexts, setConfirmationTexts] = useState<string[]>([]);

    const isConfirmDisabled = !expectedInputTexts.every((text) => confirmationTexts.includes(text));

    useImperativeHandle(dialogRef, () => ({
        openDialog: () => {
            setConfirmationTexts([]);
            setOpen(true);
        }
    }));

    const hasErrorForIndex = (index: number) => {
        const text = expectedInputTexts[index];
        const confirmationTextForIndex = confirmationTexts[index] || '';
        return confirmationTextForIndex !== '' && confirmationTextForIndex !== text;
    };

    const handleConfirm = async () => {
        if (isConfirmDisabled) return;
        if (!waitForConfirm) {
            onConfirm?.();
            setOpen(false);
            return;
        }

        await onConfirm?.();
        setOpen(false);
        setConfirmationTexts([]);
    };

    const handleClose = () => {
        onClose?.();
        setOpen(false);
        setConfirmationTexts([]);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth={isMobile}
            fullScreen={false}
            className="dialog-container"
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: '3px !important',
                    minWidth: isMobile ? 'auto' : '400px',
                    maxWidth: '650px',
                    minHeight: isMobile ? 'auto' : '300px',
                    maxHeight: '700px'
                }
            }}
        >
            <Box sx={{ p: isMobile ? 1 : 3 }}>
                <DialogTitle
                    sx={{
                        textAlign: 'center',
                        fontWeight: 400,
                        fontSize: '1rem',
                        letterSpacing: '1.4px',
                        textTransform: 'uppercase',
                        py: 2,
                        px: 0
                    }}
                >
                    {heading}
                </DialogTitle>

                <DialogContent sx={{ p: isMobile ? 0 : 3 }}>
                    <DialogContentText>
                        <Typography
                            sx={{
                                textAlign: 'center',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-line',
                                fontSize: '14px',
                                px: isMobile ? 0 : 4,
                                py: isMobile ? 2 : 4
                            }}
                        >
                            {message}
                        </Typography>
                    </DialogContentText>
                    <Box
                        sx={{
                            maxHeight: '250px',
                            overflowY: 'auto'
                        }}
                    >
                        <Box sx={{ px: isMobile ? 1 : 6, gap: 4, display: 'flex', flexDirection: 'column' }}>
                            {expectedInputTexts.map((text, index) => (
                                <Box key={index}>
                                    <WcnTextField
                                        sx={{ width: '100%' }}
                                        key={index}
                                        value={confirmationTexts[index] || ''}
                                        onChange={(e) => {
                                            const newConfirmationTexts = [...confirmationTexts];
                                            newConfirmationTexts[index] = e.target.value;
                                            setConfirmationTexts(newConfirmationTexts);
                                        }}
                                        label={t('typeToConfirmName', { name: text })}
                                        placeholder={text}
                                        variant="outlined"
                                        size="small"
                                        error={hasErrorForIndex(index)}
                                        autoComplete="off"
                                        helperText={
                                            hasErrorForIndex(index) ? t('mustTypeNameToConfirm', { name: text }) : ''
                                        }
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: isMobile ? 1 : 3, py: 3, gap: 2, justifyContent: 'center' }}>
                    <WcnButton onClick={handleClose} variant="red" style={{ flex: 1 }}>
                        {cancelText ? t(cancelText) : t('no')}
                    </WcnButton>

                    <WcnButton
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        variant="primary"
                        style={{ flex: 1 }}
                    >
                        {isSaving ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <CircularProgress size={14} />
                                <span>{confirmText ? t(confirmText) : t('confirm')}</span>
                            </Box>
                        ) : confirmText ? (
                            t(confirmText)
                        ) : (
                            t('confirm')
                        )}
                    </WcnButton>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

ConfirmationDialogRequireInput.displayName = 'ConfirmationDialogRequireInput';

export default ConfirmationDialogRequireInput;
